import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export const sectionLabel = "text-[11px] font-semibold uppercase tracking-wider text-zinc-400";
const chip = "inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border";
const KORT = { omzet: "omzet", pk: "PK", bijverkoop: "bijverkoop", wow: "WOW" };
export const rodeKpis = (c) => Object.keys(KORT).filter((f) => c?.[f] === "niet" || c?.[f] === "nee").map((f) => KORT[f]);
export const cijfersVan = (c) => ["omzet", "pk", "bijverkoop", "wow"].filter((f) => c?.[f]).length;

// B3: de ingeklapte kaart zegt hoe hij ervoor staat.
export function KopChips({ call, info }) {
  const loc = call.locatie;
  const n = call.cijfers?.beantwoord ?? 0;
  const a = info?.aangeleverd;
  const v = info?.vorige;
  // v21-R1 R6: groen alleen als de call ook klaar is (vanaf de nieuwe regels); anders neutraal.
  const groen = n === 4 && (!call.staat?.nieuweRegels || call.staat.klaar);
  return (
    <>
      {call.updatedAt && call.cijfers && (
        <span data-testid={`chip-cijfers-${loc}`} className={`${chip} ${groen ? "bg-emerald-50 text-emerald-700 border-emerald-200" : n === 4 ? "bg-zinc-100 text-zinc-600 border-zinc-200" : n > 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
          {n === 0 ? "Cijfers ontbreken" : `Cijfers ${n}/4`}
        </span>
      )}
      {call.isRood && (
        <span data-testid={`rood-badge-${loc}`} className={`${chip} bg-rose-600 text-white border-rose-600`}>
          {call.roodZonderAfspraak ? "Rood zonder afspraak" : "Rood"} · {rodeKpis(call).join(", ")}{!call.roodZonderAfspraak && " · afspraak loopt"}
        </span>
      )}
      {a && a.status !== "geen-account" && (
        <span data-testid={`chip-aangeleverd-${loc}`} className={`${chip} ${a.status === "volledig" ? "bg-zinc-100 text-zinc-500 border-zinc-200" : a.status === "niets" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
          {a.status === "open" ? "Nog aan te leveren" : a.status === "nog-niet-open" ? "Venster nog niet open" : a.status === "niets" ? "Locatie leverde niets aan" : a.totaal > 0 ? `Aangeleverd ${a.ingevuld}/${a.totaal}` : "Aangeleverd"}
        </span>
      )}
      {v?.rood && (
        <span data-testid={`chip-vorige-${loc}`} className={`${chip} bg-white text-rose-700 border-rose-200`}>
          Week {v.overWeekNr} rood · {v.rodeKpis.join(", ")}
        </span>
      )}
      {info?.compleetDoor && (
        <span data-testid={`chip-compleet-door-${loc}`} className={`${chip} bg-zinc-100 text-zinc-500 border-zinc-200 normal-case`}>
          Klaar door {info.compleetDoor.charAt(0).toUpperCase() + info.compleetDoor.slice(1)}
        </span>
      )}
      {call.bevindingFlag && <span className={`${chip} bg-amber-100 text-amber-800 border-amber-200`}>Bevinding ontbreekt</span>}
    </>
  );
}

// v10-R1: één compacte foutregel met 'Opnieuw proberen' — nooit een stille laadfout.
export function FoutRegel({ tekst, onRetry, testid }) {
  return (
    <div data-testid={testid} role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
      <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0" />
      <span className="text-xs font-semibold text-rose-800">{tekst}</span>
      {onRetry && (
        <button type="button" data-testid={`${testid}-retry`} onClick={onRetry} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900 underline underline-offset-2">
          <RefreshCw className="h-3 w-3" /> Opnieuw proberen
        </button>
      )}
    </div>
  );
}

// B2-2: wat de locatie aanleverde voor deze KPI, naast je oordeel. R2: bij een laadfout geen vals feit, alleen '—'.
export function LocatieCijfer({ prep, err, kpiId, locatie }) {
  if (err) return <p data-testid={`kpi-locatie-${kpiId}-${locatie}`} className="text-xs text-zinc-400">Locatie: —</p>;
  if (!prep) return <p data-testid={`kpi-locatie-${kpiId}-${locatie}`} className="text-xs text-zinc-400">Laden…</p>;
  const v = prep.kpis?.[kpiId];
  if (prep.aangeleverd && v && (v.value || v.antwoord)) {
    return (
      <p data-testid={`kpi-locatie-${kpiId}-${locatie}`} className="text-xs text-zinc-500">
        Locatie: <span className="font-mono font-bold text-zinc-900">{v.value || "—"}</span>{v.antwoord && <span className="whitespace-pre-line"> · {v.antwoord}</span>}
      </p>
    );
  }
  const nog = !prep.aangeleverd && prep.venster && !prep.venster.gesloten;
  return <p data-testid={`kpi-locatie-${kpiId}-${locatie}`} className={`text-xs italic ${nog ? "text-amber-700" : "text-zinc-400"}`}>{nog ? "Nog aan te leveren" : "Niet aangeleverd"}</p>;
}

export function Lade({ titel, open, onToggle, testid, children }) {
  return (
    <section data-testid={testid} className="rounded-[10px] border border-zinc-200 bg-white">
      <button type="button" data-testid={`${testid}-toggle`} onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 transition-colors">
        {open ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
        <span className="text-xs font-semibold text-zinc-700">{titel}</span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </section>
  );
}

// B2-5: wat er nog loopt bij deze locatie (alleen locatie-afspraken; de server gebruikt q_locatie).
export function LooptNog({ locatie, versie, roodZonderAfspraak, editable, onAddAfspraak }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setRows(null); setErr(false);
    api.get(`/locaties/${encodeURIComponent(locatie)}/lopend`).then((r) => alive && setRows(r.data)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [locatie, versie, tick]);
  return (
    <div data-testid={`loopt-nog-${locatie}`} className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className={sectionLabel}>Loopt nog bij {locatie}</div>
        {editable && <button data-testid={`lopend-add-afspraak-${locatie}`} onClick={() => onAddAfspraak(locatie)} className="flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"><Plus className="h-3.5 w-3.5" /> Afspraak</button>}
      </div>
      {roodZonderAfspraak && (
        <div data-testid={`rood-zonder-afspraak-${locatie}`} className="flex items-center justify-between gap-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2.5">
          <p className="text-sm text-rose-800"><span className="font-bold">Rood dwingt opvolging af.</span> Maak een open afspraak voor deze locatie.</p>
          {editable && <button data-testid={`add-afspraak-${locatie}`} onClick={() => onAddAfspraak(locatie)} className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700"><Plus className="h-3.5 w-3.5" /> Afspraak</button>}
        </div>
      )}
      {err ? <FoutRegel testid={`lopend-error-${locatie}`} tekst="Afspraken konden niet geladen worden" onRetry={() => setTick((t) => t + 1)} />
        : !rows ? <p className="text-xs text-zinc-400">Laden…</p>
        : rows.length === 0 ? <p className="text-xs text-zinc-400 italic">Geen open afspraken.</p>
        : (
          <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200">
            {rows.map((a) => (
              <li key={a.id}>
                <Link to={`/afspraken?select=${a.id}`} data-testid={`lopend-${a.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-sm hover:bg-zinc-50">
                  <span className="flex-1 min-w-[160px] text-zinc-800 truncate">{a.wat}</span>
                  {a.afdelingNaam && <span data-testid={`lopend-afdeling-${a.id}`} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">{a.afdelingNaam}</span>}
                  {a.doorLocatie && <span data-testid={`lopend-door-locatie-${a.id}`} className="text-[10px] font-bold uppercase tracking-wider text-amber-700">door de locatie</span>}
                  <span className="text-xs text-zinc-500">opvolger <span className="capitalize">{a.opvolger || "geen"}</span></span>
                  {a.wachtOpOmLabel ? <span data-testid={`lopend-wacht-op-om-${a.id}`} className="font-mono text-xs text-blue-700 font-semibold">{a.wachtOpOmLabel}</span> : <span data-testid={`lopend-termijn-${a.id}`} className={`font-mono text-xs ${a.dagenTeLaat > 0 ? "text-rose-600 font-bold" : "text-zinc-500"}`}>
                    {a.deelpunt && `deelpunt ${a.deelpunt.nr} van ${a.deelpunt.van} · `}{a.volgendeLabel || "geen deadline"}
                    {a.dagenTeLaat > 0 && ` · ${a.dagenTeLaat} ${a.dagenTeLaat === 1 ? "dag" : "dagen"} te laat`}
                  </span>}
                </Link>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

// v13 A3/A4: één rij per check: vinkje (tijdens de call), het antwoord van de locatie met "Neem over", jouw antwoord en wat je vorige keer schreef.
function LocatieAntwoord({ p, prep, prepErr, locatie, kanOvernemen, onOvernemen }) {
  const bl = ((prep?.antwoorden || {})[p.id] || "").trim();
  const opm = ((prep?.opmerkingen || {})[p.id] || "").trim();
  const open = prep?.venster && !prep.venster.gesloten;
  const leegTekst = open ? "nog aan te leveren" : prep?.aangeleverd ? "geen antwoord" : "niets aangeleverd";
  return (
    <div data-testid={`prep-locatie-${p.id}-${locatie}`} className="flex items-start gap-2 text-xs text-zinc-500">
      <p className="flex-1 min-w-0 whitespace-pre-line break-words">
        <span className="font-semibold text-zinc-500">Locatie: </span>
        {prepErr ? "—" : !prep ? "Laden…" : bl ? bl : <span className="italic">{leegTekst}</span>}
        {bl && opm && <span className="block text-zinc-500">Opmerking: {opm}</span>}
      </p>
      {kanOvernemen && bl && (
        <button type="button" data-testid={`prep-overnemen-${p.id}-${locatie}`} onClick={() => onOvernemen(bl)}
          className="shrink-0 min-h-[44px] sm:min-h-[32px] px-2 rounded border border-blue-200 text-xs font-semibold text-blue-700 hover:border-blue-400 hover:text-blue-900 transition-colors">Neem over</button>
      )}
    </div>
  );
}

// v13-R1 F2: "meer" zodra de tekst echt is afgekapt (gemeten, ook na resize), dus ook op een smalle telefoon.
function VorigeKeer({ v, testid }) {
  const [open, setOpen] = useState(false);
  const [geklemd, setGeklemd] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const meet = () => { const el = ref.current; if (el && !open) setGeklemd(el.scrollHeight > el.clientHeight + 1); };
    meet();
    window.addEventListener("resize", meet);
    return () => window.removeEventListener("resize", meet);
  }, [v, open]);
  if (!v) return null;
  return (
    <div data-testid={testid} className="text-xs text-zinc-400">
      <p ref={ref} className={`whitespace-pre-line break-words ${open ? "" : "line-clamp-2"}`}>
        Vorige keer ({v.callLabel}): {v.tekst}{v.opmerking && <span className="block">Opmerking: {v.opmerking}</span>}
      </p>
      {(geklemd || open) && <button type="button" data-testid={`${testid}-meer`} onClick={() => setOpen((o) => !o)} className="min-h-[44px] sm:min-h-0 font-semibold text-zinc-500 hover:text-zinc-800">{open ? "minder" : "meer"}</button>}
    </div>
  );
}

// v18 B1: op de telefoon is een check één rij (vinkje + tekst + kort antwoord); tik op de tekst = open/dicht.
function CheckKop({ p, local, editable, locatie, setPrep, compact, uit, onToggle, wie, antw }) {
  const vink = <input type="checkbox" disabled={!editable} checked={!!local.prep?.[p.id]} onChange={(e) => setPrep(p.id, e.target.checked)} data-testid={`prep-${p.id}-${locatie}`} className="mt-0.5 h-4 w-4 rounded-sm accent-blue-600" />;
  const tekst = <span className="text-sm text-zinc-700">{p.text}{p.label && <span data-testid={`prep-label-${p.id}-${locatie}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{p.label}</span>}</span>;
  if (!compact) return <label className="flex items-start gap-2.5 cursor-pointer">{vink}{tekst}</label>;
  return (
    <div className="flex items-start gap-1">
      <label className="shrink-0 min-h-[44px] min-w-[36px] flex items-start pt-0.5 cursor-pointer">{vink}</label>
      <button type="button" onClick={onToggle} aria-expanded={uit} data-testid={`check-toggle-${p.id}-${locatie}`} className="flex-1 min-w-0 min-h-[44px] flex items-start gap-2 text-left">
        <span className="flex-1 min-w-0">
          {tekst}
          {!uit && (
            <span data-testid={`check-kort-${p.id}-${locatie}`} className="block text-xs text-zinc-500 truncate mt-0.5">
              <span className="font-semibold">{wie}: </span>{antw.trim() ? antw.trim().replace(/\s+/g, " ") : <span className="italic text-amber-700">nog geen antwoord</span>}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-zinc-400 transition-transform ${uit ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}

export function CheckRij({ p, local, prep, prepErr, editable, locatie, inputCls, vorige, wie = "Jij", eigen = true, setPrep, setPrepNote, setPrepOpmerking, flushNow, compact = false, uit = true, onToggle }) {
  const antw = (local.prepNotes || {})[p.id] || "";
  const opm = (local.prepOpmerkingen || {})[p.id] || "";
  const leeg = !antw.trim();
  const [opmOpen, setOpmOpen] = useState(!!opm);
  useEffect(() => { if (opm) setOpmOpen(true); }, [opm]);   // v13-R1 F5: eenmaal open blijft het veld staan, ook leeg
  return (
    <li data-testid={`check-rij-${p.id}-${locatie}`} data-open={compact ? String(uit) : undefined} className={`px-3 space-y-1.5 ${compact ? "py-1.5" : "py-2.5"}`}>
      <CheckKop p={p} local={local} editable={editable} locatie={locatie} setPrep={setPrep} compact={compact} uit={uit} onToggle={onToggle} wie={wie} antw={antw} />
      {(!compact || uit) && <div className={`ml-6 space-y-1.5 ${compact ? "pb-2" : ""}`}>
        <LocatieAntwoord p={p} prep={prep} prepErr={prepErr} locatie={locatie} kanOvernemen={editable && leeg} onOvernemen={(bl) => setPrepNote(p.id, bl)} />
        {editable ? (
          <>
            <div className="flex items-start gap-2">
              <span className="mt-2 text-xs font-semibold text-zinc-500 min-w-[1.5rem] shrink-0">{wie}</span>
              <textarea rows={2} placeholder="Jouw antwoord" value={antw} onChange={(e) => setPrepNote(p.id, e.target.value)} onBlur={flushNow} data-testid={`prep-note-${p.id}-${locatie}`}
                className={`${inputCls} ${leeg ? "border-amber-400 ring-1 ring-amber-100" : ""}`} />
            </div>
            {opmOpen ? (
              <div className="ml-8">
                <input maxLength={300} placeholder="Opmerking (optioneel)" value={opm} onChange={(e) => setPrepOpmerking(p.id, e.target.value)} onBlur={flushNow} data-testid={`prep-opmerking-${p.id}-${locatie}`} className={inputCls} />
                <div className="text-[10px] text-zinc-400 text-right mt-0.5" data-testid={`prep-opmerking-teller-${p.id}-${locatie}`}>{opm.length}/300</div>
              </div>
            ) : (
              <button type="button" data-testid={`prep-opmerking-open-${p.id}-${locatie}`} onClick={() => setOpmOpen(true)} className="ml-8 min-h-[44px] sm:min-h-0 text-xs font-semibold text-blue-700 hover:text-blue-900">+ opmerking</button>
            )}
          </>
        ) : (
          <p data-testid={`prep-antwoord-${p.id}-${locatie}`} className="text-xs text-zinc-700 whitespace-pre-line break-words">
            <span className="font-semibold text-zinc-500">{wie}: </span>{antw.trim() || <span className={`italic ${eigen ? "text-amber-700" : "text-zinc-400"}`}>nog geen antwoord</span>}
            {opm.trim() && <span className="block text-zinc-500">Opmerking: {opm}</span>}
          </p>
        )}
        <VorigeKeer v={vorige} testid={`prep-vorige-${p.id}-${locatie}`} />
      </div>}
    </li>
  );
}

export const nietDezeWeekTekst = (lijst) => lijst.map((c) => `${c.text}${c.label ? ` (${c.label})` : ""}`).join(", ");
